// Image-to-MIDI translator (admin Training Ground).
//
// Workflow:
//   1. Drop a screenshot of a piano roll.
//   2. The vision LLM detects each note rectangle and returns
//      { bpm, key, mode, notes:[{step,pitch,duration}] }.
//   3. The detected notes populate an INTERACTIVE 16-step piano roll grid
//      so the admin can correct any AI interpretation errors before playback.
//   4. "Play" triggers the (possibly edited) grid notes through the selected
//      instrument — the Melody Kit's priority sampler, with a synth fallback.
import React, { useEffect, useMemo, useRef, useState } from "react";
import API from "../api";
import genPlayer from "../audio/GenPlayer";
import melodyKitManager from "../audio/melodyKitManager";
import { toast } from "sonner";
import { UploadSimple, Image as ImageIcon, Play, SpinnerGap, Trash, Eraser } from "@phosphor-icons/react";
import * as Tone from "tone";

const INSTRUMENTS = ["piano", "lead", "flute", "brass", "strings"];
const STEPS = 16;

// Piano roll vertical range. We render top→bottom (B4 at the top, C2 at the
// bottom) so higher pitches sit higher on the grid, matching standard DAW
// conventions. 3 octaves = 36 keys, which fits comfortably without scrolling
// for a 1-bar pattern.
const NOTE_NAMES = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
const PITCHES = (() => {
  const arr = [];
  for (let oct = 5; oct >= 2; oct--) {
    for (let i = NOTE_NAMES.length - 1; i >= 0; i--) {
      arr.push(`${NOTE_NAMES[i]}${oct}`);
    }
  }
  return arr; // top = C5, bottom = C2  → 48 rows
})();
const isBlackKey = (p) => p.includes("#");
const ROW_HEIGHT = 14;
const STEP_WIDTH = 28;

// Stable note id helper.
const nid = () => `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Build a "uid'd" copy of the notes returned by the backend so React can key
// them and we can mutate single notes without touching the rest.
const annotate = (notes) => (notes || []).map((n) => ({
  id: nid(),
  step: Math.max(0, Math.min(STEPS - 1, Math.floor(Number(n.step) || 0))),
  pitch: typeof n.pitch === "string" ? n.pitch : "C4",
  duration: Math.max(1, Math.floor(Number(n.duration) || 1)),
}));

export default function ImageToMidiPanel() {
  const fileInputRef = useRef(null);
  const gridRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [meta, setMeta] = useState(null); // { bpm, key, mode } — not editable here
  const [notes, setNotes] = useState([]); // editable, drives playback
  const [instrument, setInstrument] = useState("piano");
  const [playing, setPlaying] = useState(false);
  const [playingStep, setPlayingStep] = useState(-1);

  // Drag-to-resize state (resize tail of a note by dragging its right edge)
  const [resizing, setResizing] = useState(null); // { id, startStep, startDur, startX }

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Only PNG / JPEG / WEBP images are accepted"); return;
    }
    if (file.size > 4 * 1024 * 1024) { toast.error("Image must be ≤ 4 MB"); return; }
    setBusy(true);
    setMeta(null);
    setNotes([]);
    setPreviewSrc(URL.createObjectURL(file));
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("instrument", instrument);
      const { data } = await API.post("/training/image-to-midi", form, { headers: { "Content-Type": "multipart/form-data" }, params: { instrument } });
      const midi = data.midi || {};
      setMeta({ bpm: midi.bpm || 120, key: midi.key || "C", mode: midi.mode || "major" });
      setNotes(annotate(midi.notes));
      toast.success(`Detected ${(midi.notes || []).length} notes · ${midi.key || "?"} ${midi.mode || ""}`);
    } catch (e) {
      toast.error("Parse failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  // ----- Grid editing ------------------------------------------------------
  // Click an empty cell → add a 1-step note. Click an existing note → delete.
  const cellClick = (pitch, step, e) => {
    e.preventDefault();
    e.stopPropagation();
    const existing = notes.find((n) => n.pitch === pitch && n.step <= step && n.step + n.duration > step);
    if (existing) {
      setNotes((arr) => arr.filter((n) => n.id !== existing.id));
    } else {
      setNotes((arr) => [...arr, { id: nid(), pitch, step, duration: 1 }]);
    }
  };

  const startResize = (note, e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ id: note.id, startStep: note.step, startDur: note.duration, startX: e.clientX });
  };

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const dx = e.clientX - resizing.startX;
      const deltaSteps = Math.round(dx / STEP_WIDTH);
      const newDur = Math.max(1, Math.min(STEPS - resizing.startStep, resizing.startDur + deltaSteps));
      setNotes((arr) => arr.map((n) => n.id === resizing.id ? { ...n, duration: newDur } : n));
    };
    const onUp = () => setResizing(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [resizing]);

  const removeNote = (id, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setNotes((arr) => arr.filter((n) => n.id !== id));
  };

  const clearGrid = () => setNotes([]);

  // ----- Playback ----------------------------------------------------------
  const playMidi = async () => {
    if (playing || notes.length === 0) return;
    setPlaying(true);
    try {
      await Tone.start();
      await melodyKitManager.refresh().catch(() => {});
      const bpm = meta?.bpm || 120;
      const secPerStep = 60 / bpm / 4;
      const t0 = Tone.now() + 0.05;
      // Schedule step indicator
      for (let s = 0; s < STEPS; s++) {
        setTimeout(() => setPlayingStep(s), s * secPerStep * 1000);
      }
      // Schedule notes
      for (const n of notes) {
        const t = t0 + n.step * secPerStep;
        const dur = Math.max(0.05, n.duration * secPerStep);
        if (!melodyKitManager.trigger(n.pitch, t, dur, 0.85)) {
          try { genPlayer.synths?.lead?.triggerAttackRelease(n.pitch, dur, t, 0.85); } catch { /* noop */ }
        }
      }
      const totalSec = (STEPS * secPerStep) + 0.4;
      setTimeout(() => { setPlaying(false); setPlayingStep(-1); }, totalSec * 1000);
    } catch (e) {
      toast.error("Playback failed: " + e.message);
      setPlaying(false);
      setPlayingStep(-1);
    }
  };

  // ----- Auto-scroll the grid to center the bulk of the notes -------------
  useEffect(() => {
    if (!notes.length || !gridRef.current) return;
    const idxs = notes.map((n) => PITCHES.indexOf(n.pitch)).filter((i) => i >= 0);
    if (!idxs.length) return;
    const avg = idxs.reduce((a, b) => a + b, 0) / idxs.length;
    const target = Math.max(0, avg * ROW_HEIGHT - gridRef.current.clientHeight / 2);
    gridRef.current.scrollTo({ top: target, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes.length]);

  const noteCount = notes.length;
  const headerInfo = useMemo(() => {
    if (!meta) return null;
    return `${meta.key} ${meta.mode} · ${meta.bpm} bpm · ${noteCount} notes`;
  }, [meta, noteCount]);

  return (
    <div className="rounded-2xl p-5 space-y-4" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }} data-testid="image-to-midi-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2">
          <ImageIcon size={14} weight="fill" /> Image → MIDI translator
        </h2>
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-mono uppercase text-white/40">Instrument</label>
          <select
            value={instrument}
            onChange={(e) => setInstrument(e.target.value)}
            className="bg-[#0a0a0a] border border-[#262626] text-xs px-2 py-1.5 rounded-md"
            data-testid="img2midi-instrument"
          >
            {INSTRUMENTS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* ----- Image dropzone ----- */}
        <div
          onClick={() => !busy && fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
          className="lg:col-span-2 rounded-lg p-4 cursor-pointer flex flex-col items-center justify-center gap-2 transition"
          style={{ background: previewSrc ? "transparent" : "#070707", border: "1.5px dashed #2a2a2a", minHeight: 200 }}
          data-testid="img2midi-dropzone"
        >
          {busy ? (
            <div className="flex items-center gap-2 text-sm text-white/70"><SpinnerGap size={16} weight="bold" className="animate-spin" /> Parsing piano-roll image…</div>
          ) : previewSrc ? (
            <img src={previewSrc} alt="uploaded piano roll" className="max-h-56 rounded-md" data-testid="img2midi-preview" />
          ) : (
            <>
              <UploadSimple size={22} weight="bold" className="text-white/40" />
              <div className="text-sm text-white/60">Drop a piano-roll screenshot here</div>
              <div className="text-[10px] text-white/30 font-mono">PNG / JPEG / WEBP · ≤ 4 MB · 1-bar 16-step grid</div>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="img2midi-file-input"
          />
        </div>

        {/* ----- Interactive piano roll ----- */}
        <div className="lg:col-span-3 rounded-lg p-2 space-y-2" style={{ background: "#070707", border: "1px solid #1f1f1f" }} data-testid="img2midi-grid-wrap">
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="text-[10px] font-mono text-white/50 truncate" data-testid="img2midi-meta">
              {headerInfo || "no image yet — drop one ⇠ to populate the grid, or click cells below to author manually"}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={clearGrid}
                disabled={!notes.length}
                className="px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-1 disabled:opacity-40 hover:bg-white/10"
                style={{ color: "#f97373" }}
                title="Clear all notes"
                data-testid="img2midi-clear"
              >
                <Eraser size={11} weight="bold" /> clear
              </button>
              <button
                onClick={playMidi}
                disabled={playing || !notes.length}
                className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: "linear-gradient(180deg,#22c55e,#16a34a)", color: "#0b1f10" }}
                data-testid="img2midi-play"
              >
                <Play size={12} weight="fill" /> {playing ? "Playing…" : `Play through ${instrument}`}
              </button>
            </div>
          </div>

          {/* Grid container — scrollable, fixed height */}
          <div
            ref={gridRef}
            className="relative overflow-auto rounded-md select-none"
            style={{ background: "#050505", border: "1px solid #1a1a1a", maxHeight: 280 }}
            data-testid="img2midi-grid"
          >
            <div style={{ position: "relative", width: 48 + STEP_WIDTH * STEPS, height: ROW_HEIGHT * PITCHES.length }}>
              {/* Pitch labels + row backgrounds */}
              {PITCHES.map((p, rowIdx) => (
                <div
                  key={p}
                  className="absolute left-0 right-0 flex"
                  style={{ top: rowIdx * ROW_HEIGHT, height: ROW_HEIGHT }}
                >
                  <div
                    className="text-[9px] font-mono flex items-center justify-end pr-1"
                    style={{
                      width: 48,
                      background: isBlackKey(p) ? "#0a0a0a" : "#101010",
                      color: p.startsWith("C") ? "#a3e635" : "#52525b",
                      borderRight: "1px solid #1a1a1a",
                      borderBottom: "1px solid #141414",
                    }}
                  >
                    {p}
                  </div>
                  <div className="flex" style={{ width: STEP_WIDTH * STEPS }}>
                    {Array.from({ length: STEPS }).map((_, s) => (
                      <div
                        key={s}
                        onClick={(e) => cellClick(p, s, e)}
                        style={{
                          width: STEP_WIDTH,
                          height: ROW_HEIGHT,
                          background: isBlackKey(p) ? "#0c0c0c" : "#111",
                          borderRight: (s + 1) % 4 === 0 ? "1px solid #262626" : "1px solid #141414",
                          borderBottom: "1px solid #141414",
                          cursor: "pointer",
                        }}
                        data-testid={`img2midi-cell-${p}-${s}`}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* Playhead */}
              {playingStep >= 0 && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: 48 + playingStep * STEP_WIDTH,
                    top: 0,
                    width: STEP_WIDTH,
                    height: ROW_HEIGHT * PITCHES.length,
                    background: "rgba(34,197,94,0.08)",
                    borderLeft: "1px solid #22c55e",
                  }}
                />
              )}

              {/* Notes */}
              {notes.map((n) => {
                const rowIdx = PITCHES.indexOf(n.pitch);
                if (rowIdx < 0) return null;
                return (
                  <div
                    key={n.id}
                    className="absolute rounded-sm group"
                    style={{
                      left: 48 + n.step * STEP_WIDTH + 1,
                      top: rowIdx * ROW_HEIGHT + 1,
                      width: STEP_WIDTH * n.duration - 2,
                      height: ROW_HEIGHT - 2,
                      background: "linear-gradient(180deg,#34d399,#10b981)",
                      boxShadow: "0 0 6px rgba(16,185,129,0.4)",
                      cursor: "pointer",
                    }}
                    onClick={(e) => removeNote(n.id, e)}
                    title={`${n.pitch} · step ${n.step} · ${n.duration} step(s) · click to delete`}
                    data-testid={`img2midi-note-${n.id}`}
                  >
                    <div
                      onMouseDown={(e) => startResize(n, e)}
                      className="absolute right-0 top-0 h-full"
                      style={{ width: 4, cursor: "ew-resize", background: "rgba(0,0,0,0.4)" }}
                      data-testid={`img2midi-note-${n.id}-resize`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between text-[10px] text-white/40 font-mono px-1">
            <span>click empty cell → add · click note → delete · drag right edge → resize</span>
            <span data-testid="img2midi-note-count">{noteCount} note{noteCount === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
